import type { RichTextConfig } from '@/core/models/richText';
import { angularSanitizeHtml } from './sanitize';

/**
 * The behaviour of @kolkov/angular-editor 2.1.0 (MIT) without Angular: `AngularEditorComponent`,
 * `AngularEditorService` and the logic of `AngularEditorToolbarComponent`, ported statement by statement.
 *
 * The editing itself is the browser's: a `contenteditable` element driven by `document.execCommand`, exactly like the
 * original. That is what makes the saved HTML identical (bare first line, `<div>` for the following lines,
 * `<b>`/`<i>`/`<u>`/`<strike>`, `<font size|color|face>`, `<blockquote>` for indent, ... and the same paste, undo and
 * caret behaviour) since it is the same engine producing it; a model-based editor such as Tiptap cannot reproduce
 * Chrome's execCommand output. See PARITY_DECISIONS D10.
 */

const HEADING_COMMANDS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'pre'];
const BLOCK_SELECT = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'PRE', 'DIV'];
/** Buttons whose `active` state follows `queryCommandState`. */
const STATE_BUTTONS = [
  'bold',
  'italic',
  'underline',
  'strikeThrough',
  'subscript',
  'superscript',
  'justifyLeft',
  'justifyCenter',
  'justifyRight',
  'justifyFull',
  'indent',
  'outdent',
  'insertUnorderedList',
  'insertOrderedList',
  'link',
];
const TAG_MAP: Record<string, string> = { BLOCKQUOTE: 'indent', A: 'link' };

export interface EngineOptions {
  /** Latest config (read on every use; angular-editor reads `this.config` live too). */
  getConfig: () => RichTextConfig;
  /** The `id` input of the component (`''` when absent); button ids are `<name>-<id>`. */
  id: string;
  /** ControlValueAccessor `onChange`; only set while the editor is bound to a model. */
  onChange?: (html: string) => void;
  /** Called whenever state that the toolbar renders has changed (Angular change detection). */
  notify: () => void;
}

/** `Renderer2.addClass/removeClass` on an element found by id (the first one in the document, like Angular). */
function setClass(id: string, name: string, on: boolean): void {
  const element = document.getElementById(id);
  if (element) element.classList.toggle(name, on);
}

export class AngularEditorEngine {
  textArea!: HTMLElement;
  wrapper!: HTMLElement;
  onChange?: (html: string) => void;

  // component state
  modeVisual = true;
  showPlaceholder = false;
  disabled = false;
  focused = false;
  touched = false;
  changed = false;

  // service state
  savedSelection: Range | null = null;
  selectedText = '';

  // toolbar state
  htmlMode = false;
  linkSelected = false;
  block = 'default';
  fontName = 'Times New Roman';
  fontSize = '3';

  private focusInstance?: () => void;
  private blurInstance?: () => void;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(private readonly options: EngineOptions) {
    this.onChange = options.onChange;
  }

  private get config(): RichTextConfig {
    return this.options.getConfig();
  }

  private get id(): string {
    return this.options.id;
  }

  private get toolbarShown(): boolean {
    const showToolbar = this.config.showToolbar;
    return showToolbar !== undefined ? !!showToolbar : true;
  }

  /** `executeInNextQueueIteration`: setTimeout(cb, 100). */
  private later(callback: () => void, timeout = 100): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      callback();
      this.options.notify();
    }, timeout);
    this.timers.add(timer);
  }

  dispose(): void {
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
    this.focusInstance?.();
    this.blurInstance?.();
  }

  // ---- AngularEditorService -------------------------------------------------------------------------------------

  saveSelection = (): void => {
    if (document.getSelection) {
      const sel = document.getSelection();
      if (sel && sel.getRangeAt && sel.rangeCount) {
        this.savedSelection = sel.getRangeAt(0);
        this.selectedText = sel.toString();
      }
    } else {
      this.savedSelection = null;
    }
  };

  private restoreSelection(): boolean {
    if (this.savedSelection) {
      const sel = document.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(this.savedSelection);
        return true;
      }
      return false;
    }
    return false;
  }

  private serviceExecuteCommand(command: string, value?: string): void {
    if (HEADING_COMMANDS.includes(command)) {
      document.execCommand('formatBlock', false, command);
      return;
    }
    document.execCommand(command, false, value);
  }

  private insertHtml(html: string): void {
    const inserted = document.execCommand('insertHTML', false, html);
    if (!inserted) throw new Error('Unable to perform the operation');
  }

  private createLink(url: string): void {
    if (!url.includes('http')) {
      document.execCommand('createlink', false, url);
    } else {
      this.insertHtml(`<a href="${url}" target="_blank">${this.selectedText}</a>`);
    }
  }

  private nextNode(node: Node | null): Node | null {
    if (!node) return null;
    if (node.hasChildNodes()) return node.firstChild;
    let current: Node | null = node;
    while (current && !current.nextSibling) current = current.parentNode;
    if (!current) return null;
    return current.nextSibling;
  }

  private getRangeSelectedNodes(range: Range, includePartiallySelectedContainers: boolean): Node[] {
    let node: Node | null = range.startContainer;
    const endNode = range.endContainer;
    let rangeNodes: Node[] = [];
    if (node === endNode) {
      rangeNodes = [node];
    } else {
      while (node && node !== endNode) {
        node = this.nextNode(node);
        rangeNodes.push(node as Node);
      }
      node = range.startContainer;
      while (node && node !== range.commonAncestorContainer) {
        rangeNodes.unshift(node);
        node = node.parentNode;
      }
    }
    if (includePartiallySelectedContainers) {
      node = range.commonAncestorContainer;
      while (node) {
        rangeNodes.push(node);
        node = node.parentNode;
      }
    }
    return rangeNodes;
  }

  private getSelectedNodes(): Node[] {
    const nodes: Node[] = [];
    const sel = document.getSelection();
    if (sel) {
      for (let i = 0, len = sel.rangeCount; i < len; ++i) {
        nodes.push(...this.getRangeSelectedNodes(sel.getRangeAt(i), true));
      }
    }
    return nodes;
  }

  private replaceWithOwnChildren(el: Element): void {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.hasChildNodes()) parent.insertBefore(el.firstChild as Node, el);
    parent.removeChild(el);
  }

  private removeSelectedElements(tagNames: string): void {
    const tagNamesArray = tagNames.toLowerCase().split(',');
    this.getSelectedNodes().forEach((node) => {
      if (node && node.nodeType === 1 && tagNamesArray.indexOf((node as Element).tagName.toLowerCase()) > -1) {
        this.replaceWithOwnChildren(node as Element);
      }
    });
  }

  private insertYouTubeVideoTag(videoUrl: string): void {
    const id = videoUrl.split('v=')[1];
    const imageUrl = `https://img.youtube.com/vi/${id}/0.jpg`;
    const thumbnail = `
      <div style='position: relative'>
        <a href='${videoUrl}' target='_blank'>
          <img src="${imageUrl}" alt="click to watch"/>
          <img style='position: absolute; left:200px; top:140px'
          src="https://img.icons8.com/color/96/000000/youtube-play.png"/>
        </a>
      </div>`;
    this.insertHtml(thumbnail);
  }

  private insertVimeoVideoTag(videoUrl: string): void {
    // Angular used HttpClient; no app interceptor (auth header) belongs on a call to vimeo.com.
    fetch(`https://vimeo.com/api/oembed.json?url=${videoUrl}`)
      .then((response) => response.json())
      .then((data: { thumbnail_url_with_play_button: string; title: string }) => {
        const imageUrl = data.thumbnail_url_with_play_button;
        const thumbnail = `<div>
        <a href='${videoUrl}' target='_blank'>
          <img src="${imageUrl}" alt="${data.title}"/>
        </a>
      </div>`;
        this.insertHtml(thumbnail);
      })
      .catch(() => undefined);
  }

  private serviceInsertVideo(videoUrl: string): void {
    if (videoUrl.match('www.youtube.com')) this.insertYouTubeVideoTag(videoUrl);
    if (videoUrl.match('vimeo.com')) this.insertVimeoVideoTag(videoUrl);
  }

  // ---- AngularEditorComponent -----------------------------------------------------------------------------------

  /** Host `(focus)`. */
  onHostFocus = (): void => {
    this.focus();
  };

  /** `<angular-editor ... autofocus>` is not used by any screen; kept for completeness. */
  focus(): void {
    if (this.modeVisual) {
      this.textArea.focus();
    } else {
      const sourceText = document.getElementById(`sourceText${this.id}`);
      sourceText?.focus();
      this.focused = true;
    }
  }

  /** Toolbar `triggerCommand(command)` -> component `executeCommand(command, value)`. */
  executeCommand = (command: string, value?: string): void => {
    this.focus();
    if (command === 'focus') return;
    if (command === 'toggleEditorMode') {
      this.toggleEditorMode(this.modeVisual);
    } else if (command !== '') {
      if (command === 'clear') {
        this.removeSelectedElements('span');
        this.onContentChange(this.textArea);
      } else if (command === 'default') {
        this.removeSelectedElements('h1,h2,h3,h4,h5,h6,p,pre');
        this.onContentChange(this.textArea);
      } else {
        this.serviceExecuteCommand(command, value);
      }
      this.exec();
    }
    this.options.notify();
  };

  onTextAreaFocus = (event: Event): void => {
    if (this.focused) {
      event.stopPropagation();
      return;
    }
    this.focused = true;
    if (!this.touched || !this.changed) {
      this.later(() => {
        this.configure();
        this.touched = true;
      });
    }
  };

  onTextAreaMouseOut = (): void => {
    this.saveSelection();
    this.options.notify();
  };

  onTextAreaBlur = (event: FocusEvent): void => {
    this.later(this.saveSelection);
    if (event.relatedTarget !== null) {
      const parent = (event.relatedTarget as Element).parentElement;
      if (
        parent &&
        !parent.classList.contains('angular-editor-toolbar-set') &&
        !parent.classList.contains('ae-picker')
      ) {
        this.focused = false;
      }
    }
  };

  /** `(input)`. `element` is the event target (the `<code>` element while the HTML source is shown). */
  onContentChange = (element: HTMLElement): void => {
    let html = this.modeVisual ? element.innerHTML : element.innerText;
    if (!html || html === '<br>') html = '';
    if (typeof this.onChange === 'function') {
      const config = this.config;
      const emitted = config.sanitize || config.sanitize === undefined ? (angularSanitizeHtml(html) ?? '') : html;
      // registerOnChange: `e === '<br>' ? fn('') : fn(e)`
      this.onChange(emitted === '<br>' ? '' : emitted);
      if (!html !== this.showPlaceholder) this.togglePlaceholder(this.showPlaceholder);
    }
    this.changed = true;
    this.options.notify();
  };

  /** ControlValueAccessor.writeValue. */
  writeValue(value: string | null | undefined): void {
    if ((!value || value === '<br>' || value === '') !== this.showPlaceholder) {
      this.togglePlaceholder(this.showPlaceholder);
    }
    if (value === undefined || value === '' || value === '<br>') value = null;
    // A saved value is cleaned before it is shown, so a script or event handler stored by another route never runs.
    this.textArea.innerHTML = value === null || value === undefined ? '' : (angularSanitizeHtml(value) ?? '');
  }

  togglePlaceholder(value: boolean): void {
    if (!value) {
      this.wrapper.classList.add('show-placeholder');
      this.showPlaceholder = true;
    } else {
      this.wrapper.classList.remove('show-placeholder');
      this.showPlaceholder = false;
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.textArea.classList.toggle('disabled', isDisabled);
    this.disabled = isDisabled;
  }

  toggleEditorMode(bToSource: boolean): void {
    const editableElement = this.textArea;
    if (bToSource) {
      const oContent = document.createTextNode(editableElement.innerHTML);
      editableElement.innerHTML = '';
      editableElement.setAttribute('contenteditable', 'false');
      const oPre = document.createElement('pre');
      oPre.style.setProperty('margin', '0');
      oPre.style.setProperty('outline', 'none');
      const oCode = document.createElement('code');
      oCode.id = `sourceText${this.id}`;
      oCode.style.setProperty('display', 'block');
      oCode.style.setProperty('white-space', 'pre-wrap');
      oCode.style.setProperty('word-break', 'keep-all');
      oCode.style.setProperty('outline', 'none');
      oCode.style.setProperty('margin', '0');
      oCode.style.setProperty('background-color', '#fff5b9');
      oCode.setAttribute('contenteditable', 'true');
      oCode.appendChild(oContent);
      const onFocus = (event: Event) => this.onTextAreaFocus(event);
      const onBlur = (event: Event) => this.onTextAreaBlur(event as FocusEvent);
      oCode.addEventListener('focus', onFocus);
      oCode.addEventListener('blur', onBlur);
      this.focusInstance = () => oCode.removeEventListener('focus', onFocus);
      this.blurInstance = () => oCode.removeEventListener('blur', onBlur);
      oPre.appendChild(oCode);
      editableElement.appendChild(oPre);
      document.execCommand('defaultParagraphSeparator', false, 'div');
      this.modeVisual = false;
      oCode.focus();
    } else {
      editableElement.innerHTML = editableElement.innerText;
      editableElement.setAttribute('contenteditable', 'true');
      this.modeVisual = true;
      this.onContentChange(editableElement);
      editableElement.focus();
    }
    this.setEditorMode(!this.modeVisual);
  }

  /** Toggles the toolbar buttons and the block select when the cursor moves. */
  exec(): void {
    this.triggerButtons();
    let userSelection: Selection | null = null;
    if (document.getSelection) {
      userSelection = document.getSelection();
      this.later(this.saveSelection);
    }
    let a: Node | null = userSelection?.focusNode ?? null;
    const els: Node[] = [];
    while (a && (a as Element).id !== 'editor') {
      els.unshift(a);
      a = a.parentNode;
    }
    this.triggerBlocks(els);
  }

  private configure(): void {
    const config = this.config;
    if (config.defaultParagraphSeparator) {
      document.execCommand('defaultParagraphSeparator', false, config.defaultParagraphSeparator);
    }
    const defaults = config as RichTextConfig & { defaultFontName?: string; defaultFontSize?: string };
    if (defaults.defaultFontName) document.execCommand('fontName', false, defaults.defaultFontName);
    if (defaults.defaultFontSize) document.execCommand('fontSize', false, defaults.defaultFontSize);
  }

  // ---- AngularEditorToolbarComponent ----------------------------------------------------------------------------

  get isLinkButtonDisabled(): boolean {
    return this.htmlMode || !this.selectedText;
  }

  /** Highlights the editor buttons when the cursor moves. */
  triggerButtons(): void {
    if (!this.toolbarShown) return;
    STATE_BUTTONS.forEach((name) => {
      let result: boolean;
      try {
        result = document.queryCommandState(name);
      } catch {
        result = false;
      }
      setClass(`${name}-${this.id}`, 'active', result);
    });
  }

  /** Highlights the editor buttons and updates the selects for the block the cursor is in. */
  triggerBlocks(nodes: Node[]): void {
    if (!this.toolbarShown) return;
    this.linkSelected = nodes.findIndex((x) => x.nodeName === 'A') > -1;
    let found = false;
    BLOCK_SELECT.forEach((y) => {
      const node = nodes.find((x) => x.nodeName === y);
      if (node !== undefined && y === node.nodeName) {
        if (found === false) {
          this.block = node.nodeName.toLowerCase();
          found = true;
        }
      } else if (found === false) {
        this.block = 'default';
      }
    });
    Object.keys(TAG_MAP).forEach((tag) => {
      const node = nodes.find((x) => x.nodeName === tag);
      setClass(`${TAG_MAP[tag]}-${this.id}`, 'active', node !== undefined && tag === node.nodeName);
    });
    // read only for their side effects on the editing style, like angular-editor (`foreColour`, `backColor`)
    document.queryCommandValue('ForeColor');
    this.fontSize = document.queryCommandValue('FontSize');
    this.fontName = document.queryCommandValue('FontName').replace(/"/g, '');
    document.queryCommandValue('backColor');
  }

  triggerCommand(command: string): void {
    this.executeCommand(command);
  }

  insertUrl(): void {
    let url: string | null = 'https://';
    const selection = this.savedSelection;
    const parentElement = selection?.commonAncestorContainer.parentElement;
    if (selection && parentElement?.nodeName === 'A') {
      if ((parentElement as HTMLAnchorElement).href !== '') url = (parentElement as HTMLAnchorElement).href;
    }
    url = window.prompt('Insert URL link', url);
    if (url && url !== '' && url !== 'https://') this.createLink(url);
    this.options.notify();
  }

  insertVideo(): void {
    this.executeCommand('');
    const url = window.prompt('Insert Video link', 'https://');
    if (url && url !== '' && url !== 'https://') this.serviceInsertVideo(url);
  }

  insertColor(color: string, where: 'textColor' | 'backgroundColor'): void {
    const restored = this.restoreSelection();
    if (restored) {
      if (where === 'textColor') document.execCommand('foreColor', false, color);
      else document.execCommand('hiliteColor', false, color);
    }
    this.executeCommand('');
  }

  setFontName(name: string): void {
    document.execCommand('fontName', false, name);
    this.executeCommand('');
  }

  setFontSize(size: string): void {
    document.execCommand('fontSize', false, size);
    this.executeCommand('');
  }

  setEditorMode(mode: boolean): void {
    setClass(`toggleEditorMode-${this.id}`, 'active', mode);
    this.htmlMode = mode;
  }

  /** `(change)` of the hidden `<input type=file>`: base64 insert (no uploadUrl / upload function in any config). */
  onFileChanged(input: HTMLInputElement): void {
    const file = input.files?.[0];
    if (file && file.type.includes('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        document.execCommand('insertImage', false, String(reader.result));
      };
      reader.readAsDataURL(file);
    }
  }

  /** Angular's `isButtonHidden`. */
  isButtonHidden(name: string): boolean {
    return isButtonHidden(this.config.toolbarHiddenButtons, name);
  }
}

/** Angular's `isButtonHidden(name)` over `config.toolbarHiddenButtons`. */
export function isButtonHidden(hiddenButtons: string[][] | undefined, name: string): boolean {
  if (!name) return false;
  if (!(hiddenButtons instanceof Array)) return false;
  let result: string | undefined;
  for (const arr of hiddenButtons) {
    if (arr instanceof Array) result = arr.find((item) => item === name);
    if (result) break;
  }
  return result !== undefined;
}
